import merge from 'lodash/merge';
import { addMany } from './helpers';
import { hasProductRedemptionsRemaining } from '../utils/redemptions';

const state = () => ({
  allIds: [],
  byId: {}
});

/* ----------------------------------------
 * Helpers
 * ------------------------------------- */
function compareProducts(a, b) {
  if ((a.type === 'coupon') !== (b.type === 'coupon'))
    return a.type === 'coupon' ? 1 : -1;

  if (a.redemptions !== b.redemptions)
    return a.redemptions < b.redemptions ? -1 : 1;

  return a.name.localeCompare(b.name);
}

/* ----------------------------------------
 * Getters
 * ------------------------------------- */
const getters = {
  find: (state, getters, rootState, rootGetters) => (id, dealId) =>
    getters.getCounts({
      ...state.byId[id],
      order_products: rootGetters['orderProducts/byProductId'](id, dealId)
    }),

  getById: state => id => state.byId[id] ?? null,

  byVenueId: (state, getters, rootState, rootGetters) => (venueId, dealId) => {
    let productIds = state.allIds;

    if (dealId !== undefined) {
      const orderProducts = rootGetters['orderProducts/byDealId'](dealId);
      productIds = productIds.filter(id =>
        orderProducts.some(op => op.product_id === id)
      );
    }

    return productIds
      .filter(id => state.byId[id].venue_id === Number(venueId))
      .map(id => getters.find(id, dealId))
      .sort(compareProducts);
  },

  standalone: (state, getters, rootState, rootGetters) => {
    const standaloneOPs = rootGetters['orderProducts/byDealId'](0);
    return getters.list.filter(p =>
      standaloneOPs.some(op => op.product_id === p.id)
    );
  },

  getCounts: () => product => {
    const redeemedCount =
      product.redeemedCount ||
      product?.order_products?.filter(op => op?.redeemed).length ||
      0;

    const unRedeemedCount = Math.max(
      product.unRedeemedCount ||
        (product?.order_products?.length || 0) - redeemedCount,
      0
    );

    const pointsToBeEarned =
      product.order_products?.reduce(
        (n, op) => (n > op.points_to_be_earned ? n : op.points_to_be_earned),
        0
      ) || 0;

    return {
      ...product,
      points_to_be_earned: pointsToBeEarned,
      redeemedCount,
      unRedeemedCount
    };
  },

  // "Can this passholder still use this activity?" — they need a usable order_product AND the
  // activity's own Max Redemptions cap must not be exhausted. The cap is checked here rather than in
  // each consumer so the lists, the quiz auto-check-in and the check-in button all agree.
  hasRedemptionsRemaining:
    (state, getters, rootState, rootGetters) =>
    (product, dealId = null) => {
      if (!hasProductRedemptionsRemaining(product)) return false;

      let orderProducts =
        rootGetters['orderProducts/byProductId'](product.id);

      if (dealId)
        orderProducts = orderProducts.filter(op => op.deal_id === dealId);

      return orderProducts.some(
        op =>
          (!op.redeemed || product.duplicate_date) &&
          (!op.deal_id ||
            rootGetters['orderProducts/hasRedemptionsRemaining'](op))
      );
    },

  list: (state, getters) => state.allIds.map(id => getters.find(id))
};

/* ----------------------------------------
 * Actions
 * ------------------------------------- */
const actions = {
  addProducts({ commit }, products) {
    commit('addMany', products);
  },

  updateQuizSettings({ commit }, { productId, quizSettings }) {
    commit('updateQuizSettings', { productId, quizSettings });
  }
};

/* ----------------------------------------
 * Mutations
 * ------------------------------------- */
const mutations = {
  add(state, product) {
    if (!state.allIds.includes(product.id)) {
      state.byId[product.id] = product;
      state.allIds.push(product.id);
    } else {
      state.byId[product.id] = merge(
        state.byId[product.id],
        product
      );
    }
  },

  addMany(state, items) {
    addMany(state, items);
  },

  updateQuizSettings(state, { productId, quizSettings }) {
    const product = state.byId[productId];

    if (product?.settings?.quiz) {
      product.settings.quiz = merge(
        product.settings.quiz,
        quizSettings
      );
    }
  }
};

/* ----------------------------------------
 * Export
 * ------------------------------------- */
export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
};
